import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { CaseActionType as OptionType } from "interfaces/case-action-type";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetCaseActionTypes } from "hooks/case-action-types/useLazyGetCaseActionTypes";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface CaseActionTypeFilterProps {
  selectedCaseActions: OptionType[];
  onChangeSelectedCaseActions: (values: OptionType[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All",
  order: 0
};

export const CaseActionTypeFilter = ({
  selectedCaseActions,
  onChangeSelectedCaseActions,
  showChips = true
}: CaseActionTypeFilterProps): JSX.Element => {
  const {
    loading: manufacturerLoading,
    error: manufacturerError,
    fetchCaseActionType: getCaseActionTypes
  } = useLazyGetCaseActionTypes();
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getCaseActionTypes("ordering=name").then(response => {
        if (response.results) {
          setOptions([selectAllOption, ...response.results]);
        }
      });
    }
    setSelectedState(selectedCaseActions);
  }, [options, getCaseActionTypes, selectedCaseActions]);

  return (
    <StyledAutocomplete
      label="Next Action"
      placeholder={showChips ? "Next Action" : ""}
      loading={manufacturerLoading}
      error={manufacturerError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedCaseActions}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");
        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedCaseActions(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedCaseActions, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedCaseActions(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedCaseActions(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
